// utils/stepsStore.ts
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface StepHistory {
  question_id: number;
  current: number[];
  completed: number[];
  in_process: number[];
}

interface StepsState {
  step_history: StepHistory | null;

  // Set / Get / Clear
  setStepHistory: (history: StepHistory) => void;
  getStepHistory: (questionId: number) => StepHistory | null;
  clearStepHistory: () => void;

  // Current
  addCurrentStep: (questionId: number, stepId: number) => void;
  getCurrentSteps: () => number[];
  clearCurrentSteps: () => void;

  // Completed
  addCompletedStep: (questionId: number, stepId: number) => void;
  removeCompletedStep: (stepId: number) => void;
  getCompletedSteps: () => number[];
  clearCompletedSteps: () => void;

  // In process
  markStepInProcess: (questionId: number, stepId: number) => void;
}

export const useStepsStore = create(
  persist<StepsState>(
    (set, get) => ({
      step_history: null,

      // Set / Get / Clear
      setStepHistory: (history) =>
        set({
          step_history: {
            question_id: history.question_id,
            current: history.current || [],
            completed: history.completed || [],
            in_process: history.in_process || [],
          },
        }),

      getStepHistory: (questionId) => {
        const history = get().step_history;
        if (!history || history.question_id !== questionId) return null;
        return history;
      },

      clearStepHistory: () => set({ step_history: null }),

      // Current
      addCurrentStep: (questionId, stepId) => {
        const history = get().step_history;
        if (history && history.question_id === questionId) {
          const updatedHistory: StepHistory = {
            ...history,
            current: history.current.includes(stepId)
              ? history.current
              : [...history.current, stepId],
          };
          set({ step_history: updatedHistory });
        } else {
          set({
            step_history: {
              question_id: questionId,
              current: [stepId],
              completed: [],
              in_process: [],
            },
          });
        }
      },
      getCurrentSteps: () => get().step_history?.current || [],
      clearCurrentSteps: () =>
        set((state) =>
          state.step_history
            ? { step_history: { ...state.step_history, current: [] } }
            : state
        ),

      // Completed
      addCompletedStep: (questionId, stepId) => {
        const history = get().step_history;
        if (history && history.question_id === questionId) {
          const updatedHistory: StepHistory = {
            ...history,
            completed: history.completed.includes(stepId)
              ? history.completed
              : [...history.completed, stepId],
            in_process: history.in_process.filter((id) => id !== stepId),
            current: history.current.filter((id) => id !== stepId),
          };
          set({ step_history: updatedHistory });
        }
      },
      removeCompletedStep: (stepId) => {
        const history = get().step_history;
        if (history) {
          const updatedHistory: StepHistory = {
            ...history,
            completed: history.completed.filter((id) => id !== stepId),
          };
          set({ step_history: updatedHistory });
        }
      },
      getCompletedSteps: () => get().step_history?.completed || [],
      clearCompletedSteps: () =>
        set((state) =>
          state.step_history
            ? { step_history: { ...state.step_history, completed: [] } }
            : state
        ),

      // In process
      markStepInProcess: (questionId, stepId) => {
        const history = get().step_history;
        if (history && history.question_id === questionId) {
          const updatedHistory: StepHistory = {
            ...history,
            in_process: history.in_process.includes(stepId)
              ? history.in_process
              : [...history.in_process, stepId],
            completed: history.completed.filter((id) => id !== stepId),
          };
          set({ step_history: updatedHistory });
        } else {
          set({
            step_history: {
              question_id: questionId,
              current: [],
              completed: [],
              in_process: [stepId],
            },
          });
        }
      },
    }),
    {
      name: "shoonit-question-steps",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
